class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    window.activeRanges = window.activeRanges || {};

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.updateRangeLabels();

    this.addEventListener('change', (event) => {
      // console.log(event, event.target, event.details);
      if (event.target.tagName !== 'DUAL-RANGE') return;

      const container = event.target.closest('[data-container]');
      if (!container) return;

      const fromInput = container.querySelector('input[name$="_from0"]');
      const toInput = container.querySelector('input[name$="_to0"]');

      if (!fromInput || !toInput) return;

      fromInput.value = event.detail.min;
      toInput.value = event.detail.max;
    });
  }

  connectedCallback() {
    this.moreFiltersButton = this.querySelector('.facets__more');
    if (!this.moreFiltersButton) return;
    this.overflowContainer = this.closest('.facets-container');
    this.overflowEvents = new AbortController();
    const options = { signal: this.overflowEvents.signal };
    this.scheduleOverflowLayout = () => {
      cancelAnimationFrame(this.overflowFrame);
      this.overflowFrame = requestAnimationFrame(() => this.updateOverflowLayout());
    };
    this.moreFiltersButton.addEventListener('click', () => {
      const drawer = this.overflowContainer.querySelector('#FacetFiltersFormMobile')?.closest('menu-drawer');
      if (!drawer) return;
      this.overflowDrawer = drawer;
      this.openedOverflowDrawer = true;
      this.moreFiltersButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('bg-facets-drawer-open');
      drawer.querySelector(':scope > details > summary').click();
      drawer.querySelector('.mobile-facets__close').focus();
    }, options);
    this.overflowContainer.addEventListener('toggle', (event) => {
      if (event.target !== this.overflowDrawer?.mainDetailsToggle || event.target.open) return;
      this.moreFiltersButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('bg-facets-drawer-open');
      if (this.openedOverflowDrawer) {
        this.openedOverflowDrawer = false;
        const target = this.getBoundingClientRect().width
          ? (this.moreFiltersButton.hidden ? this.querySelector('summary') : this.moreFiltersButton)
          : this.overflowDrawer.querySelector('summary');
        target?.focus();
      }
    }, { ...options, capture: true });
    window.addEventListener('resize', this.scheduleOverflowLayout, options);
    this.overflowResizeObserver = new ResizeObserver(this.scheduleOverflowLayout);
    this.overflowResizeObserver.observe(this);
    document.fonts.ready.then(() => { if (this.isConnected) this.scheduleOverflowLayout(); });
    this.scheduleOverflowLayout();
  }

  disconnectedCallback() {
    this.overflowEvents?.abort();
    this.overflowResizeObserver?.disconnect();
    cancelAnimationFrame(this.overflowFrame);
    if (this.openedOverflowDrawer) this.closeOverflowDrawer();
  }

  closeOverflowDrawer() {
    this.overflowDrawer?.closeMenuDrawer(new Event('close'));
    if (this.overflowDrawer) this.overflowDrawer.mainDetailsToggle.open = false;
    document.body.classList.remove('bg-facets-drawer-open');
    this.moreFiltersButton?.setAttribute('aria-expanded', 'false');
  }

  updateOverflowLayout() {
    const form = this.querySelector('#FacetFiltersForm');
    const filters = [...form.querySelectorAll('#FacetsWrapperDesktop > .facets__disclosure')];
    const sorting = form.querySelector('.facet-filters.sorting');
    const button = this.moreFiltersButton;
    filters.forEach(filter => { filter.hidden = false; });
    button.hidden = true;
    form.removeAttribute('data-overflow-ready');
    if (!this.getBoundingClientRect().width) {
      if (this.openedOverflowDrawer) this.closeOverflowDrawer();
      this.overflowContainer.classList.remove('facets-container--overflow');
      return;
    }

    // Measure the labels before distributing spare width across the visible controls.
    form.setAttribute('data-facets-measuring', '');
    button.hidden = false;
    const widths = filters.map(filter => filter.getBoundingClientRect().width);
    const sortingWidth = sorting?.getBoundingClientRect().width || 0;
    const available = form.clientWidth;
    const needsDrawer = widths.reduce((sum, width) => sum + width, sortingWidth) > available;
    const filterSpace = available - sortingWidth - button.getBoundingClientRect().width;
    let used = 0;
    filters.forEach((filter, index) => {
      used += widths[index];
      filter.hidden = needsDrawer && used > filterSpace;
      if (filter.hidden && filter.open) {
        if (filter.contains(document.activeElement)) button.focus();
        filter.open = false;
        filter.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }
    });
    button.hidden = !needsDrawer;
    form.removeAttribute('data-facets-measuring');
    form.setAttribute('data-overflow-ready', '');
    if (!needsDrawer && this.openedOverflowDrawer) this.closeOverflowDrawer();
    this.overflowContainer.classList.toggle('facets-container--overflow', needsDrawer);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    document.querySelector('.facets__more')?.closest('facet-filters-form').scheduleOverflowLayout();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });


    document.querySelectorAll(".filter-btn").forEach((element, index) => {
      let selector = ".filter-btn";
      if(document.querySelector(selector) && html.querySelector(selector)) {
        document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
      }
    });

    document.querySelectorAll(".filter-count").forEach((element, index) => {
      let selector = "." + element.classList[1];
      if(document.querySelector(selector) && html.querySelector(selector)) {
        document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
      }
    });

    document.querySelectorAll(".mobile-filter-content").forEach((element, index) => {
      let selector = "." + element.classList[1];
      
      if(document.querySelector(selector) && html.querySelector(selector)) {
        document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  updateRangeLabels() {
    if (!window.activeRanges) return;

    document.querySelectorAll('facet-remove-all > span').forEach(pill => {
      const name = pill.dataset.name;
      const range = window.activeRanges[name];

      if (!range) return;

      const labelEl = pill.querySelector('.js-range-label');
      if (!labelEl) return;

      labelEl.textContent = `${range.from} – ${range.to}`;
    });
  }

  createSearchParams(form) {
    // const formData = new FormData(form);
    // return new URLSearchParams(formData).toString();
    const rangeInputs = form.querySelectorAll('input[name$="_from0"], input[name$="_to0"]');

    rangeInputs.forEach(input => input.disabled = true);

    const formData = new FormData(form);
    const params = new URLSearchParams(formData).toString();

    rangeInputs.forEach(input => input.disabled = false);

    return params;
  }

  createSearchParams0(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  setRangeValues(event) {
    const container = event.target.closest('[data-container]');
    const rangeEl = container.querySelector('filter-range');
    const form = event.target.closest('form');

    const fromInput = rangeEl.querySelector('input[name$="_from0"]');
    const toInput = rangeEl.querySelector('input[name$="_to0"]');

    if (event.srcElement.className === 'dual-range') {
      if (fromInput) {
        fromInput.value = event.target.values.min;
        fromInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (toInput) {
        toInput.value = event.target.values.max;
        toInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (!fromInput || !toInput) return;


    const baseName = fromInput.name.replace(/_from0$/, '');
    const minAllowed = Number(fromInput.getAttribute('data-min')) || 0;
    const maxAllowed = Number(toInput.getAttribute('data-max')) || Infinity;

    let from = parseInt(fromInput.value, 10);
    let to = parseInt(toInput.value, 10);

    // Auto-fill missing side
    if (Number.isNaN(from) && !Number.isNaN(to)) {
      from = minAllowed;
      fromInput.value = from;
    }

    if (Number.isNaN(to) && !Number.isNaN(from)) {
      to = maxAllowed;
      toInput.value = to;
    }

    // Still invalid → just return current params
    if (Number.isNaN(from) || Number.isNaN(to) || from > to) {
      return this.createSearchParams(form);
    }

    window.activeRanges[baseName] = { from, to };

    // Parse allowed values
    const rawValues = (rangeEl.dataset.range || '').split(',').map(v => v.trim()).filter(Boolean);

    const matchedValues = rawValues.filter(value => {
      // "5+" → include ONLY if base number is inside range
      if (/^\d+\+$/.test(value)) {
        const num = parseInt(value, 10);
        return num >= from && num <= to;
      }

      // plain number ("5")
      if (/^\d+$/.test(value)) {
        const num = parseInt(value, 10);
        return num >= from && num <= to;
      }

      // text labels ("From 10 years old", etc.)
      const textMatch = value.match(/(\d+)/);
      if (textMatch) {
        const num = parseInt(textMatch[1], 10);
        return num >= from && num <= to;
      }

      return false;
    });

    // Remove previous discrete values
    form.querySelectorAll(`input[name="${baseName}"]`).forEach(el => el.remove());

    // Add matched discrete values
    matchedValues.forEach(value => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.className = "filter-inputs";
      input.name = baseName;
      input.value = value;
      form.appendChild(input);
    });

    const searchParams = this.createSearchParams(form);
    return searchParams;
  }
  
  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    if (event.target.closest('[data-container]')?.querySelector('filter-range')) {
      let searchParams = this.setRangeValues(event);

      this.onSubmitForm(searchParams, event);
      return;

    } else if (event.srcElement.className === 'dual-range') {
      const container = event.target.closest('[data-container]');
      const minInput = container.querySelector('[name="filter.v.price.gte"]');
      const maxInput = container.querySelector('[name="filter.v.price.lte"]');

      if (minInput) {
        minInput.value = event.target.values.min;
        minInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (maxInput) {
        maxInput.value = event.target.values.max;
        maxInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;

    } else if (event.target.type === 'radio') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);

    } else if (event.srcElement.className === 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);

    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach(form => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetFiltersForm' ||
            form.id === 'FacetSortDrawerForm'
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });

      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event, name, fullUrl) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    let sourceUrl = null;

    // Case 1: native facet link (<a>)
    if (event.currentTarget?.href) {
      sourceUrl = event.currentTarget.href;
      FacetFiltersForm.renderPage((event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1)));
      return;
    }

    // Case 2: custom remove-all (<span>)
    if (!sourceUrl && fullUrl) {
      sourceUrl = fullUrl;
    }

    if (!sourceUrl || !name) return;

    const urlObj = new URL(sourceUrl, window.location.origin);
    const params = urlObj.searchParams;

    // Remove ALL values for this filter key
    params.delete(name);

    const finalUrl = params.toString() ? `${urlObj.pathname}?${params.toString()}` : urlObj.pathname;

    document.querySelectorAll(`.filter-inputs[name="${ name }"]`).forEach(el => el.remove());

    FacetFiltersForm.renderPage(params.toString());
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FilterRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
  
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('filter-range', FilterRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    if (this.classList.contains('active-facets__all')) {
      document.querySelectorAll('input.filter-inputs').forEach(el => el.remove());
    }

    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');

    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class FacetRemoveAll extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('span');

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event, this.querySelector(`[data-name]`).dataset.name, this.querySelector(`[data-name]`).dataset.url);
  }
}

customElements.define('facet-remove-all', FacetRemoveAll);